import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, TextField, Typography, Divider } from "@mui/material";
import { toUtcIso, toLocalDateInputValue, fromLocalDateTime, addDays, formatTimeLocal } from "./utils";
import { EmployeeService } from "../../service/employeeService";
import SimpleDatePicker from "../ui/simpleDatePicker";
import SimpleTimePicker from "../ui/simpleTimePicker";
import { usePreferences } from "hooks/usePreferences";

export default function TimeOffDialog({ open, practitionerId, activeDate, onSave, onDelete, onClose, mode = 'create', timeOff = null, schedules = null, locationId = null, showMsg = null }) {
      const { preferences } = usePreferences();
      const tenantTimezone = preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Date range + time range with weekday selection
  const [form, setForm] = useState({ 
    startDate: toLocalDateInputValue(activeDate, tenantTimezone),
    endDate: toLocalDateInputValue(activeDate, tenantTimezone),
    startTime: '12:00',
    endTime: '13:00',
    weekdays: { 0: true, 1: true, 2: true, 3: true, 4: true, 5: true, 6: true },
    allDay: false,
    reason: "Lunch break"
  });
  const [prName, setPrName] = useState("");
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && timeOff) {
        const s = new Date(timeOff.startUtc);
        const e = new Date(timeOff.endUtc);
        // Detect all-day: start 00:00 and end 23:59 on same local date, or end 00:00 next day
        const sDate = toLocalDateInputValue(s, tenantTimezone);
        const eDate = toLocalDateInputValue(e, tenantTimezone);
        const sTime = formatTimeLocal(s);
        const eTime = formatTimeLocal(e);
        const allDay = (sTime === '00:00' && eTime === '23:59' && eDate === sDate) ||
                       (sTime === '00:00' && eTime === '00:00' && eDate === toLocalDateInputValue(addDays(s, 1), tenantTimezone));
        setForm({
          startDate: toLocalDateInputValue(s, tenantTimezone),
          endDate: toLocalDateInputValue(s, tenantTimezone),
          startTime: allDay ? '00:00' : sTime,
          endTime: allDay ? '23:59' : eTime,
          weekdays: { [s.getDay()]: true },
          allDay,
          reason: timeOff.reason || 'Time Off'
        });
      } else {
        setForm({
          startDate: toLocalDateInputValue(activeDate, tenantTimezone),
          endDate: toLocalDateInputValue(activeDate, tenantTimezone),
          startTime: '12:00',
          endTime: '13:00',
          weekdays: { 0: true, 1: true, 2: true, 3: true, 4: true, 5: true, 6: true },
          allDay: false,
          reason: "Lunch break"
        });
      }
      EmployeeService.getPractitioners()
        .then(res => {
          if (res?.code === 0 && Array.isArray(res.data)) {
            const p = res.data.find(x => x.id === practitionerId);
            setPrName(p?.fullName || `#${practitionerId || ''}`);
          }
        })
        .catch(() => setPrName(practitionerId ? `#${practitionerId}` : ""));
    }
  }, [open, activeDate, practitionerId, mode, timeOff]);

  const daysOfWeek = useMemo(() => ([
    { key: 0, label: 'Sun' },
    { key: 1, label: 'Mon' },
    { key: 2, label: 'Tue' },
    { key: 3, label: 'Wed' },
    { key: 4, label: 'Thu' },
    { key: 5, label: 'Fri' },
    { key: 6, label: 'Sat' },
  ]), []);

  const handleSave = () => {
    // Validate
    if (!form.startDate || !form.endDate || !form.startTime || !form.endTime) { onClose(); return; }
    const sd = form.startDate; // 'yyyy-MM-dd'
    const ed = form.endDate;   // 'yyyy-MM-dd'
    if (!sd || !ed || ed < sd) { onClose(); return; }
    
    // Get practitioner's schedule for filtering
    const scheduleKey = practitionerId && locationId ? `${practitionerId}-${locationId}` : null;
    const practitionerSchedule = scheduleKey && schedules ? schedules.get(scheduleKey) : null;
    
    // end must be after start within a day
    // Build payloads for each day in range matching selected weekdays
    const items = [];
    const skippedDays = []; // Track days skipped due to no schedule
    
    // iterate by date strings in clinic TZ to avoid off-by-one
    const toDateObj = (s) => new Date(`${s}T00:00:00`);
    let curStr = sd;
    while (curStr <= ed) {
      const curDate = toDateObj(curStr);
      const dow = curDate.getDay();
      if (form.weekdays[dow]) {
        // Check if practitioner has a schedule for this day of week
        const daySchedule = practitionerSchedule?.[dow];
        const hasSchedule = daySchedule?.enabled === true;
        
        if (hasSchedule) {
          const startLocal = fromLocalDateTime(curStr, form.allDay ? '00:00' : form.startTime);
          const endLocal = fromLocalDateTime(curStr, form.allDay ? '23:59' : form.endTime);
          if (endLocal > startLocal) {
            items.push({ practitionerId, startUtc: toUtcIso(startLocal), endUtc: toUtcIso(endLocal), reason: form.reason });
          }
        } else {
          // Track skipped day
          skippedDays.push(daysOfWeek.find(d => d.key === dow)?.label || dow);
        }
      }
      // increment curStr by one day using utils addDays on Date then reformat
      const nextDay = addDays(toDateObj(curStr), 1);
      const pad = (n) => String(n).padStart(2, '0');
      curStr = `${nextDay.getFullYear()}-${pad(nextDay.getMonth()+1)}-${pad(nextDay.getDate())}`;
    }
    
    // Show feedback about skipped days
    if (skippedDays.length > 0 && showMsg) {
      const uniqueDays = [...new Set(skippedDays)].join(', ');
      showMsg(`Skipped ${uniqueDays} (no schedule)`, 'info');
    }
    
    if (items.length > 0) {
      if (mode === 'edit' && timeOff?.id) {
        onSave({ id: timeOff.id, practitionerId, items });
      } else {
        onSave(items);
      }
    } else if (showMsg) {
      showMsg('No time-off created (no active schedules for selected days)', 'warning');
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === 'edit' ? 'Edit Time Off' : 'Add Time Off / Lunch Break'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          <Typography variant="body2">Practitioner: {prName}</Typography>
          <Stack direction="row" spacing={2}>
            <SimpleDatePicker
              label="Start date"
              value={form.startDate}
              onChange={(val) => setForm({ ...form, startDate: val || '' })}
              minDate={null}
              maxDate={mode !== 'edit' ? form.endDate : null}
            />
            {mode !== 'edit' && (
              <SimpleDatePicker
                label="End date"
                value={form.endDate}
                onChange={(val) => setForm({ ...form, endDate: val || '' })}
                minDate={form.startDate || null}
                maxDate={null}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={2}>
            <SimpleTimePicker
              label="Start time"
              value={form.startTime}
              onChange={(val) => setForm({ ...form, startTime: val || '' })}
              step={15}
              disabled={!!form.allDay}
            />
            <SimpleTimePicker
              label="End time"
              value={form.endTime}
              onChange={(val) => setForm({ ...form, endTime: val || '' })}
              step={15}
              disabled={!!form.allDay}
              minTime={form.startTime || null}
            />
          </Stack>
          <FormControlLabel 
            control={<Checkbox size="small" checked={!!form.allDay} onChange={(e) => setForm({ ...form, allDay: e.target.checked, startTime: e.target.checked ? '00:00' : form.startTime, endTime: e.target.checked ? '23:59' : form.endTime })} />} 
            label={<Typography variant="caption">All day</Typography>} 
          />
          {mode !== 'edit' && (
            <Stack direction="row" spacing={1} alignItems="center">
              {daysOfWeek.map(d => (
                <FormControlLabel key={d.key} control={<Checkbox size="small" checked={!!form.weekdays[d.key]} onChange={(e) => setForm({ ...form, weekdays: { ...form.weekdays, [d.key]: e.target.checked } })} />} label={<Typography variant="caption">{d.label}</Typography>} sx={{ mr: 1 }} />
              ))}
            </Stack>
          )}
          <Divider />
          <TextField size="small" label="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        {mode === 'edit' && timeOff?.id && (
          <Button color="error" onClick={() => { if (onDelete) onDelete(timeOff.id); onClose(); }}>Delete</Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
